<template>
  <div
    id="app"
    :class="[
      'route--' + view.toLowerCase(),
      isOnTop ? 'page--is-top' : 'page--is-scrolled',
    ]"
  >
    <div
      v-if="isLoading"
      class="loading-screen"
      role="status"
      aria-label="Laster"
    >
      <div class="loading-screen__spinner"></div>
    </div>

    <template v-else>
      <Navbar :logo="logo" :logoMobile="logoMobile" :navItems="navItems" />

      <transition mode="out-in" name="route" appear>
        <router-view v-bind="content" />
      </transition>

      <Footer :footer="content.footer" />
    </template>
  </div>
</template>

<script>
import Navbar from "@/components/Navbar.vue";
import Footer from "@/components/Footer.vue";
import Logo from "@/assets/elements/logo.svg";
import logoMobile from "@/assets/elements/logomobile.svg";
import { redirect, redirectMatvarehuset, redirectKontakt } from "@/utils";
import { fetchContent } from "@/datocms.js";

export default {
  data() {
    return {
      isLoading: true,
      isOnTop: true,
      isExpanded: false,
      content: {},
      navLinks: [
        { action: redirect(["home"], { hash: "#carousel2" }, this) },
        { action: redirect(["home"], { hash: "#usps" }, this) },
        { action: redirect(["home"], { hash: "#howitworks" }, this) },
        { action: redirect(["home"], { hash: "#prices" }, this) },
        { action: redirect(["faq"], { scrollTo: 0 }, this) },
        { action: redirect(["home"], { hash: "#aboutus" }, this) },
        { action: redirect(["home"], { hash: "#delta" }, this) },
      ],
    };
  },
  computed: {
    view() {
      return this.$route.name || "";
    },
    logo() {
      return Logo;
    },
    logoMobile() {
      return logoMobile;
    },
    projectName() {
      return "Matvarehuset";
    },
    navTransition() {
      return this.view === "Campaign" ? 350 : 5;
    },
    navItems() {
      const navigation = this.content.navigation || {};

      return this.navLinks.map((link, index) => ({
        ...link,
        label: navigation[`navigationElement${index + 1}`],
      }));
    },
  },
  methods: {
    redirect,
    redirectMatvarehuset,
    redirectKontakt,
  },
  created() {
    fetchContent()
      .then((content) => {
        this.content = content;
        this.isLoading = false;
      })
      .catch((error) => console.log(error));

    const debounce = (fn) => {
      let frame;
      return (...params) => {
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => fn(...params));
      };
    };
    const storeScroll = () => {
      this.isOnTop = window.scrollY <= this.navTransition;
    };

    document.addEventListener("scroll", debounce(storeScroll), {
      passive: true,
    });
    storeScroll();
  },
  components: {
    Footer,
    Navbar,
  },
};
</script>

<style lang="scss">
@import "src/styles/main";

// #app {
//   display: flex;
//   flex-direction: column;
//   // min-height: 100vh;
// }

#app {
  min-height: 100%;
  position: relative;
}

.elder__navigation-component-icon-right {
  color: white;
}

.loading-screen {
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;

  &__spinner {
    width: 2.5rem;
    height: 2.5rem;
    border: 3px solid rgba(#c00d1e, 0.2);
    border-top-color: var(--primary);
    border-radius: 50%;

    animation: loading-spin 700ms linear infinite;
  }
}

@keyframes loading-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .loading-screen__spinner {
    animation: loading-pulse 1.2s ease-in-out infinite;
  }

  @keyframes loading-pulse {
    50% {
      opacity: 0.35;
    }
  }
}

.nav {
  position: fixed;
  background: transparent;
  box-shadow: none;

  @media (max-width: $tablet) {
    background-color: var(--secondary);
  }

  top: 0;
  width: 100%;
  z-index: 1001;

  transition:
    color 0.4s ease-in-out,
    height 0.4s ease-in-out,
    background 0.4s ease-in-out,
    backdrop-filter 0.4s ease-in-out,
    -webkit-backdrop-filter 0.4s ease-in-out,
    transform 0.4s ease-in-out,
    -webkit-transform 0.4s ease-in-out;

  &--hero {
    .page--is-top & {
      @media (max-width: $tablet) {
        position: fixed;
        background: var(--secondary);
      }

      &:not(.nav--expanded) {
        .elder__navigation-component {
          color: var(--secondary) !important;

          @media (max-width: $tablet) {
            color: var(--primary) !important;
          }
        }
      }
    }
  }

  .container {
    padding-left: 15%;
    padding-right: 15%;
    padding-top: 9px;
    padding-bottom: 9px;

    span {
      color: var(--primary-contrast);
    }

    @media (max-width: $tablet) {
      padding-left: 40px;
      padding-right: 40px;
    }

    @media (max-width: $mobile) {
      padding-left: 0px;
      padding-right: 50px;
    }
  }

  .elder__navigation-logo-image {
    width: 120px;

    @media (max-width: $mobile) {
      width: 80px;
      height: 100px;
      margin-left: 20px;
    }
  }

  .page--is-scrolled & {
    position: fixed;
    background-color: var(--secondary);
    box-shadow: 0 10px 15px -10px rgba(black, 0.05);

    .elder__navigation-logo-image {
      transition: all 400ms ease-out;

      @media (min-width: $tablet) {
        max-height: 60px !important;
      }

      @media (max-width: $tablet) {
        max-height: 70px !important;
      }
    }

    .elder__navigation-bars {
      @media (max-width: $mobile) {
        scale: 1;
        transition: all 400ms ease-out;
      }
    }
  }

  .elder__navigation-bars {
    color: var(--primary);
    scale: 1.7;
  }

  .elder__navigation {
    button {
      margin: 0;
    }

    &-logo {
      &-image {
        transition: all 400ms ease-out;

        @media (min-width: $mobile) {
          max-width: 150px;
          max-height: 7rem !important;
        }

        @media (max-width: $mobile) {
          max-height: 110px !important;
        }
      }

      .elder__navigation-component {
        &:hover {
          cursor: pointer;
        }
      }
    }

    &-actions {
      .elder__navigation-component {
        color: var(--primary);

        &:hover {
          cursor: pointer;
          background: none;

          &:after {
            content: "";
            background-color: var(--primary);
            width: calc(100% - 2rem);
            height: 2px;
            position: absolute;
            left: 1rem;
            bottom: 0.5rem;
          }
        }
      }
    }

    &--responsive {
      overflow-y: initial;
    }
  }

  &--expanded {
    height: 100vh;
    background-color: var(--secondary);

    .elder__navigation-actions {
      padding-left: 2.5rem;
      display: flex !important;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 1rem;

      span {
        font-size: 1.2rem;
      }

      .elder__navigation-node {
        // padding: 0.5rem 2rem;
      }
    }
  }

  // .elder__navigation-actions > :nth-child(5) {
  //   background-color: var(--primary);
  //   border-radius: 20px;

  //   span {
  //     color: var(--secondary-contrast);
  //   }

  //   // span {
  //   //   font-size: 1.5rem;
  //   //   font-family: var(--primary-font);
  //   //   color: var(--secondary-contrast);
  //   // }
  // }
}
.route-enter,
.route-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

.route-enter-to,
.route-leave {
  opacity: 1;
  transform: scale(1);
}

.route-enter-active,
.route-leave-active {
  transform-origin: top;
  transition: 300ms ease;
}
</style>
